import _extends from "@babel/runtime/helpers/esm/extends";
import EventTarget from '@deezer/event-target';
import { ApiAuthError, BadRequestError, CustomApiError, EmptyRequestError, NetworkError, OpaqueResponseError, ServerError } from "./errors.js";
import LegacyApiEvent, { LegacyApiEvents } from "./events.js";
const canceledRequests = [];
const callSignatures = {};
const removeSignature = signature => {
  if (callSignatures[signature]) {
    for (const {
      id: reqId
    } of callSignatures[signature]) {
      const index = canceledRequests.indexOf(reqId);
      if (index !== -1) {
        canceledRequests.splice(index, 1);
      }
    }
    delete callSignatures[signature];
  }
};
const allRequestsCanceled = (reqs, canceledReqs, type) => {
  let canceled = true;
  const fns = [];
  for (const req of reqs) {
    const {
      id: reqId,
      callbackParameters: params
    } = req;
    const fn = req[type];
    if (!canceledReqs.includes(reqId)) {
      canceled = false;
      if (typeof fn === 'function') {
        fns.push({
          fn,
          params: params !== undefined ? [params] : []
        });
      }
    }
  }
  return {
    canceled,
    fns
  };
};
export default class LegacyApi extends EventTarget {
  constructor(host, {
    fetch = globalThis.fetch,
    logger,
    credentials = 'same-origin',
    mode = 'same-origin',
    geoIP,
    arl
  } = {}) {
    super();
    this._checkForm = '';
    this._fetch = void 0;
    this._logger = void 0;
    this._host = void 0;
    this._credentials = void 0;
    this._mode = void 0;
    this._geoIP = void 0;
    this._arl = void 0;
    if (!fetch) {
      throw new TypeError('Fetch is not available on your platform or an invalid fetch was passed');
    }
    this._host = host;
    this._fetch = fetch;
    this._logger = logger;
    this._credentials = credentials;
    this._mode = mode;
    this._geoIP = geoIP;
    this._arl = arl;
    if (this._host.endsWith('/')) {
      this._host = this._host.slice(0, -1);
    }
    this._host = this._host.replace(/^https?:\/\//, '');
  }

  /**
   * Call the Deezer API
   *
   * @param {ApiOptions} options - Call options
   * @param {string} options.method - API method to call (will be serialized in the request body)
   * @param {object} [options.data={}] - Call parameters (will be serialized in the request body)
   * @param {Function|undefined} [options.success] - Callback, called if the request is successful. Will receive the JSON response
   * @param {Function|undefined} [options.error] - Callback, called if the request fails. Will receive the Error object
   * @param {Any|undefined} [options.callbackParameters] - Additonnal parameters to pass to the callback functions
   * @return {number} Request id
   */
  legacyCall({
    method,
    data = {},
    success,
    error,
    callbackParameters,
    httpHeaders = {},
    httpMethod = 'POST'
  }) {
    this.dispatchEvent(new LegacyApiEvent(LegacyApiEvents.CALL_STARTED, {
      detail: {
        method
      }
    }));
    const [url, options] = this._getRequestParameters({
      method,
      data,
      httpHeaders,
      httpMethod
    });
    const id = Number(new URL(url).searchParams.get('cid'));

    // To avoid multiple call to a same signature
    const signature = method + JSON.stringify(data) + JSON.stringify(httpHeaders);
    const requestInfo = {
      id,
      success,
      error,
      callbackParameters
    };
    callSignatures[signature] = callSignatures[signature] || [];
    callSignatures[signature].push(requestInfo);
    if (callSignatures[signature].length > 1) {
      return id;
    }

    /**
     * Request handler
     *
     * @param {object} res - Request
     * @return {Promise} Promise that resolves with a USVString object (text)
     */
    const requestHandler = function (res) {
      // Stops here if the request is unsuccessful
      // Generate a proper JSON response for the errorHandler
      if (!res.ok) {
        throw new ServerError('Api error', {
          error: {
            SERVER_ERROR: `${res.status} ${res.statusText}`
          },
          results: {}
        });
      }

      // Opaque response, can't be examined
      if (res.type === 'opaque') {
        throw new OpaqueResponseError('Api error', res);
      }
      return res.text();
    };

    /**
     * Response handler
     *
     * @param {string} rawRes - API response
     * @return {object} Object containing the callback function and its parameters
     */
    const responseHandler = rawRes => {
      var _callSignatures$signa, _res$results, _res$results2;
      const reqs = (_callSignatures$signa = callSignatures[signature]) != null ? _callSignatures$signa : [];
      const {
        canceled: allCancelled,
        fns: successfns
      } = allRequestsCanceled(reqs, canceledRequests, 'success');

      // Stop if the request was canceled
      if (allCancelled === true) {
        removeSignature(signature);
        return void 0;
      }

      // Try to parse the JSON response
      let res;
      try {
        res = JSON.parse(rawRes);
      } catch (e) {
        throw new BadRequestError('Api error', rawRes);
      }

      // Reject the response if it is not correctly formated
      if (!res || typeof res !== 'object' || typeof res.results === 'undefined' || typeof res.error === 'undefined') {
        throw new EmptyRequestError('Api error', rawRes);
      }

      // Reject the response if it contains an error
      for (const name in res.error) {
        if (!Object.hasOwnProperty.call(res.error, name)) {
          continue;
        }
        throw new CustomApiError(name, res.error[name], res);
      }

      // Reject the response if the results object is empty
      if (res.results !== null && typeof res.results === 'object' && Object.keys(res.results).length === 0) {
        throw new EmptyRequestError('Api error', rawRes);
      }

      // Keep reference of the current user checkform for API calls
      if ((_res$results = res.results) != null && _res$results.checkForm || (_res$results2 = res.results) != null && _res$results2.checkform) {
        var _res$results$checkFor;
        this._checkForm = (_res$results$checkFor = res.results.checkForm) != null ? _res$results$checkFor : res.results.checkform;
      }

      // Success
      for (const {
        fn,
        params
      } of successfns) {
        fn(res.results, ...params);
      }
      this.dispatchEvent(new LegacyApiEvent(LegacyApiEvents.CALL_SUCCESS, {
        detail: {
          method,
          data: res.results
        }
      }));
      removeSignature(signature);
    };

    /**
     * Error handler
     *
     * @param {Error} err - Error object
     * @return {object} Object containing the callback function and its parameters
     */
    const errorHandler = err => {
      var _callSignatures$signa2, _this$_logger;
      const reqs = (_callSignatures$signa2 = callSignatures[signature]) != null ? _callSignatures$signa2 : [];
      const {
        canceled: allCancelled,
        fns: errorfns
      } = allRequestsCanceled(reqs, canceledRequests, 'error');
      removeSignature(signature);

      // Stop if the request was canceled
      if (allCancelled === true) {
        return void 0;
      }

      // Invalid session, update user data to retrieve a new session and new rights
      if (err.name === 'VALID_TOKEN_REQUIRED' || err.name === 'NEED_USER_AUTH_REQUIRED') {
        err = new ApiAuthError('Invalid session, update user data to retrieve a new session and new rights');
      } else if (err instanceof TypeError) {
        err = new NetworkError(err.message);
      }

      // Use the error functions defined by the caller(s)
      if (errorfns.length) {
        for (const {
          fn,
          params
        } of errorfns) {
          fn(err, ...params);
        }
        return;
      }
      this.dispatchEvent(new LegacyApiEvent(LegacyApiEvents.CALL_ERROR, {
        detail: {
          method,
          error: err
        }
      }));

      // Unhandled error, just log it without any further action
      const extra = {
        request: data,
        response: err.response
      };
      (_this$_logger = this._logger) == null || _this$_logger.error(err.name, err, extra);
    };

    // Run query
    this._fetch.call(null, url, options)
    // Handle request
    .then(requestHandler)
    // Handle response
    .then(responseHandler)
    // Handle errors (from query, request or response handler)
    .catch(errorHandler);
    return id;
  }

  /**
   * Cancel a Deezer API request
   *
   * The request will not be stopped (since it is not yet possible to cancel a promise) but its response will not be handled.
   * It should be replaced by cancelable promise in the future (see https://domenic.github.io/cancelable-promise/)
   *
   * @param {number} id - Request id
   */
  cancel(id) {
    canceledRequests.push(id);
  }

  /**
   * Provides a promise for the Deezer API
   *
   * @param {ApiOptions} options - Call options.
   * @return {Promise}
   */
  call(options) {
    return new Promise((success, error) => {
      this.legacyCall(_extends({}, options, {
        success,
        error
      }));
    });
  }

  /**
   * Get a `Request` instance for our API.
   * @param {object} options A list of options.
   * @param {string} options.method The method to call on the API.
   * @param {object} [options.data={}] The parameters to pass to the API's method.
   * @param {object} [options.headers={}] A list of HTTP headers.
   * @param {object} [options.httpMethod='POST'] HTTP method to use
   * @return {Request}
   */
  getRequest(options) {
    return new Request(...this._getRequestParameters(options));
  }

  /**
   * Get a `Request` parameters for our API.
   * @param {object} options A list of options.
   * @param {string} options.method The method to call on the API.
   * @param {object} [options.data={}] The parameters to pass to the API's method.
   * @param {object} [options.headers={}] A list of HTTP headers.
   * @param {object} [options.httpMethod='POST'] HTTP method to use
   * @return {{0: string, 1: RequestInit}}
   */
  _getRequestParameters({
    method,
    data: body = {},
    httpHeaders = {},
    httpMethod = 'POST'
  }) {
    const protocol = typeof window !== 'undefined' && window.location.protocol === 'http:' ? 'http' : 'https'; // Fallback on https in other cases (like `file:` in Electron)

    const url = new URL(`${protocol}://${this._host}/ajax/gw-light.php`);
    const urlParameters = {
      method,
      input: 3,
      api_version: '1.0',
      api_token: method !== 'deezer.getUserData' ? encodeURIComponent(this._checkForm) : ''
    };
    if (this._geoIP) {
      urlParameters.GEOIP = this._geoIP;
    }
    if (httpMethod === 'GET') {
      urlParameters.gateway_input = JSON.stringify(body);
    } else {
      urlParameters.cid = Math.floor(Math.random() * 1e9);
    }
    if (this._arl) {
      urlParameters.arl = 'true';
      urlParameters.sid = this._arl;
    }

    // Set the URL's parameters
    Object.entries(urlParameters).forEach(([name, value]) => url.searchParams.set(name, String(value)));
    const init = {
      body: httpMethod === 'GET' ? undefined : JSON.stringify(body),
      credentials: this._credentials,
      // Needed for cookies (see https://github.com/github/fetch#sending-cookies)
      headers: httpHeaders,
      method: httpMethod,
      mode: this._mode
    };
    return [url.href, init];
  }
}
//# sourceMappingURL=LegacyApi.js.map